import type { InterviewQuestion, InterviewType } from "@scoach/types";
import { useEffect, useState } from "react";

import { interviewAdminApi } from "./api.ts";

/**
 * Admin ingestion for the two AIMS datasets. Each panel: pick a file → parse
 * (dry-run preview + warnings) → commit (full replace). Excel (.xlsx), CSV, or
 * plain text accepted.
 */
export function InterviewAdminScreen() {
  const [showAdvanced, setShowAdvanced] = useState(false);
  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: 24 }}>
      <h1>Interview data (AIMS)</h1>
      <p style={{ color: "var(--text-3)" }}>
        Upload your interview matrix (one sheet per interview type, e.g. Googleyness, Leadership).
        Uploading a type replaces only that type's questions — other types are kept.
      </p>
      <CombinedPanel />
      <button
        type="button"
        className="pill-btn"
        style={{ marginTop: 8 }}
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {showAdvanced ? "Hide" : "Show"} advanced (separate question / skill uploads)
      </button>
      {showAdvanced && (
        <>
          <QuestionsPanel />
          <TypesPanel />
        </>
      )}
    </div>
  );
}

function CombinedPanel() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{
    questions: number;
    types: { name: string; skills: number }[];
    warnings: string[];
  } | null>(null);
  const [busy, setBusy] = useState(false);

  async function doParse() {
    if (!file) return;
    setBusy(true);
    try {
      const r = await interviewAdminApi.parseCombined(file);
      setPreview({
        questions: r.questions.length,
        types: r.types.map((t) => ({
          name: t.name,
          skills: t.pillars.reduce((n, p) => n + p.kpis.length, 0),
        })),
        warnings: r.warnings.map((w) => `Row ${w.row}: ${w.message}`),
      });
    } catch (err) {
      alert(`Parse failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }
  async function doCommit() {
    if (!file) return;
    if (!confirm("Commit will replace the questions/types for the interview type(s) in this file. Continue?")) return;
    setBusy(true);
    try {
      const r = await interviewAdminApi.commitCombined(file);
      alert(`Imported ${r.importedQuestions} question(s) across ${r.importedTypes} interview type(s).`);
      setPreview(null);
      setFile(null);
    } catch (err) {
      alert(`Commit failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={card}>
      <h3 style={{ marginTop: 0 }}>Upload interview matrix</h3>
      <p style={hint}>
        Columns: Interview Focus Area · Question · Type of Question · L2–L7 (TRUE/FALSE) · Good for
        Ppl Mgr? · then one TRUE/FALSE column per skill. Excel/CSV.
      </p>
      <input type="file" accept=".xlsx,.csv,.txt" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="button" className="pill-btn" onClick={doParse} disabled={!file || busy}>
          Preview
        </button>
        <button type="button" className="pill-btn primary" onClick={doCommit} disabled={!file || busy}>
          Commit
        </button>
      </div>
      {preview && (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          <div>
            {preview.questions} question(s) across {preview.types.length} type(s):
          </div>
          {preview.types.map((t) => (
            <div key={t.name} style={{ color: "var(--text-2)" }}>
              • <strong>{t.name}</strong> — {t.skills} skill(s)
            </div>
          ))}
          {preview.warnings.slice(0, 8).map((w, i) => (
            <div key={i} style={{ color: "#f9ab00" }}>
              {w}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function QuestionsPanel() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{ count: number; warnings: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState<InterviewQuestion[]>([]);

  const refresh = () => interviewAdminApi.listQuestions().then(setExisting).catch(() => {});
  useEffect(() => {
    void refresh();
  }, []);

  async function doParse() {
    if (!file) return;
    setBusy(true);
    try {
      const r = await interviewAdminApi.parseQuestions(file);
      setPreview({ count: r.parsed.length, warnings: r.warnings.map((w) => `Row ${w.row}: ${w.message}`) });
    } catch (err) {
      alert(`Parse failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }
  async function doCommit() {
    if (!file) return;
    if (!confirm("Commit will REPLACE all existing questions. Continue?")) return;
    setBusy(true);
    try {
      const r = await interviewAdminApi.commitQuestions(file);
      alert(`Imported ${r.imported} questions.`);
      setPreview(null);
      setFile(null);
      await refresh();
    } catch (err) {
      alert(`Commit failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={card}>
      <h3 style={{ marginTop: 0 }}>Global Question Master ({existing.length})</h3>
      <p style={hint}>Columns: question, category, track(s), level, role, manager.</p>
      <input type="file" accept=".xlsx,.csv,.txt" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="button" className="pill-btn" onClick={doParse} disabled={!file || busy}>
          Preview
        </button>
        <button type="button" className="pill-btn primary" onClick={doCommit} disabled={!file || busy}>
          Commit
        </button>
      </div>
      {preview && (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          <div>{preview.count} question(s) parsed.</div>
          {preview.warnings.slice(0, 8).map((w, i) => (
            <div key={i} style={{ color: "#f9ab00" }}>
              {w}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function TypesPanel() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{ count: number; warnings: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState<InterviewType[]>([]);

  const refresh = () => interviewAdminApi.listTypes().then(setExisting).catch(() => {});
  useEffect(() => {
    void refresh();
  }, []);

  async function doParse() {
    if (!file) return;
    setBusy(true);
    try {
      const r = await interviewAdminApi.parseTypes(file);
      setPreview({ count: r.parsed.length, warnings: r.warnings.map((w) => `Row ${w.row}: ${w.message}`) });
    } catch (err) {
      alert(`Parse failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }
  async function doCommit() {
    if (!file) return;
    if (!confirm("Commit will REPLACE all existing interview types. Continue?")) return;
    setBusy(true);
    try {
      const r = await interviewAdminApi.commitTypes(file);
      alert(`Imported ${r.imported} interview type(s).`);
      setPreview(null);
      setFile(null);
      await refresh();
    } catch (err) {
      alert(`Commit failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={card}>
      <h3 style={{ marginTop: 0 }}>Interview Type &amp; Skill Matrix ({existing.length})</h3>
      <p style={hint}>
        Long-form: one row per KPI with columns interviewType, pillar, kpi, description (optional
        recommended tracks).
      </p>
      <input type="file" accept=".xlsx,.csv,.txt" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="button" className="pill-btn" onClick={doParse} disabled={!file || busy}>
          Preview
        </button>
        <button type="button" className="pill-btn primary" onClick={doCommit} disabled={!file || busy}>
          Commit
        </button>
      </div>
      {preview && (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          <div>{preview.count} interview type(s) parsed.</div>
          {preview.warnings.slice(0, 8).map((w, i) => (
            <div key={i} style={{ color: "#f9ab00" }}>
              {w}
            </div>
          ))}
        </div>
      )}
      {existing.length > 0 && (
        <ul style={{ marginTop: 12, fontSize: 13 }}>
          {existing.map((t) => (
            <li key={t.id}>
              <strong>{t.name}</strong> — {t.pillars.length} pillar(s),{" "}
              {t.pillars.reduce((n, p) => n + p.kpis.length, 0)} KPI(s)
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border-1)",
  borderRadius: 12,
  padding: 20,
  margin: "16px 0",
};
const hint: React.CSSProperties = { color: "var(--text-3)", fontSize: 13, marginTop: 0 };
